import type { ServerSideSelectionState } from '@civilgrid/ui/components';
import { useFeatureFlagEnabled } from '@posthog/react';
import { useMemo } from 'react';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { Agency } from '~/common/types';
import { Notify } from '~/components2/Notify';
import { MAX_MERGE_AGENCIES } from './mergeFields';
import { useBulkMerge } from './useBulkMerge';

// CG-614: merging is restricted to a small set of flagged users because the
// dialog can act on stale data. Mirrors AGENCY_MERGE_FLAG in
// @civilgrid/utils (client doesn't depend on that package); the API enforces
// the same flag server-side.
export const AGENCY_MERGE_FLAG = 'agency-merge-enabled';

type UseBulkActionsOptions = {
  /** All agencies currently loaded in the grid (the visible page). */
  agencies: Agency[];
  /** Called after a successful bulk action so the parent can refetch. */
  onMutationSuccess?: () => void;
};

/**
 * Result of validating a Merge bulk-action selection.
 *
 * Kept as a discriminated union so the calling hook can deterministically
 * map each outcome to (a) a user-facing toast, (b) a PostHog event, and
 * (c) whether to open the dialog. The pure shape also makes the rules
 * unit-testable without a React renderer.
 */
export type MergeSelectionEvaluation =
  | { kind: 'select-all' }
  | { kind: 'too-few' }
  | { kind: 'too-many'; count: number }
  | { kind: 'not-visible' }
  | {
      kind: 'husk-block';
      message: string;
      selectedIds: string[];
      huskIds: string[];
      targetIds: string[];
    }
  | { kind: 'inactive-block'; message: string }
  | { kind: 'ok'; selected: Agency[] };

function formatMergedIntoMessage(agency: Agency): string {
  const info = agency.mergedInto;
  if (!info) return `${agency.agencyName} is already part of a merged agency.`;
  const date = new Date(info.mergedAt).toLocaleDateString();
  return `${agency.agencyName} was already merged into ${info.targetAgencyName} on ${date}. Use ${info.targetAgencyName} instead.`;
}

/**
 * Pure decision function for the Merge bulk action.
 *
 * Checks the selection shape (Select-All unsupported, 2 to 10 rows, all on the
 * current page), then eligibility: every agency is active and not already a
 * child of a merged agency. Merged agencies are ordinary inputs.
 * `selected` follows the selection order, which sets each field's default value.
 *
 * The function is intentionally pure (no Notify / no PostHog) so the
 * rule logic can be exercised in isolation; the hook wires it up to
 * the side effects.
 */
export function evaluateMergeSelection(
  agencies: Agency[],
  selection: ServerSideSelectionState,
): MergeSelectionEvaluation {
  if (selection.mode !== 'partial') return { kind: 'select-all' };
  const { selectedIds } = selection;
  if (selectedIds.length < 2) return { kind: 'too-few' };
  if (selectedIds.length > MAX_MERGE_AGENCIES) return { kind: 'too-many', count: selectedIds.length };

  const byId = new Map(agencies.map((a) => [a.id, a]));
  const selected = selectedIds.map((id) => byId.get(id)).filter((a): a is Agency => a !== undefined);
  if (selected.length !== selectedIds.length) return { kind: 'not-visible' };

  // An agency that is already a child of a merged agency. `!= null` (loose) also
  // catches a field missing from the response (a stale client against an old server).
  const husks = selected.filter((a) => a.mergedInto != null || a.parentAgencyId != null);
  if (husks.length > 0) {
    const message = husks.map(formatMergedIntoMessage).join(' ');
    return {
      kind: 'husk-block',
      message,
      selectedIds: selected.map((a) => a.id),
      huskIds: husks.map((a) => a.id),
      targetIds: husks.map((a) => a.mergedInto?.targetId ?? a.parentAgencyId).filter((id): id is string => Boolean(id)),
    };
  }

  const inactive = selected.filter((a) => a.status !== undefined && a.status !== 'active');
  if (inactive.length > 0) {
    return {
      kind: 'inactive-block',
      message: `Only active agencies can be merged: ${inactive.map((a) => a.agencyName).join(', ')}.`,
    };
  }

  return { kind: 'ok', selected };
}

/**
 * Pure builder for the bulk-actions list. The Merge action is only offered
 * when the agency-merge-enabled flag is confirmed on; `undefined` (flags
 * still loading) and `false` both yield no actions, so the action never
 * flashes for un-flagged users. Pure for the same reason as
 * evaluateMergeSelection — unit-testable without a renderer.
 */
export function buildBulkActions(
  mergeFlagValue: boolean | undefined,
  onMergeClick: (selection: ServerSideSelectionState) => void,
) {
  if (mergeFlagValue !== true) return [];
  return [
    {
      label: 'Merge',
      icon: 'shuffle' as const,
      onClick: onMergeClick,
    },
  ];
}

export function useBulkActions({ agencies, onMutationSuccess }: UseBulkActionsOptions) {
  const merge = useBulkMerge({ onSuccess: onMutationSuccess });

  const mergeFlagValue = useFeatureFlagEnabled(AGENCY_MERGE_FLAG);

  const bulkActions = useMemo(
    () =>
      buildBulkActions(mergeFlagValue, (selection: ServerSideSelectionState) => {
        const result = evaluateMergeSelection(agencies, selection);

        switch (result.kind) {
          case 'select-all':
            Notify.error({
              message: `Select 2 to ${MAX_MERGE_AGENCIES} agencies to merge (Select-All is not supported).`,
            });
            return;
          case 'too-few':
            Notify.error({ message: `Select 2 to ${MAX_MERGE_AGENCIES} agencies to merge.` });
            return;
          case 'too-many':
            Notify.error({
              message: `You can merge at most ${MAX_MERGE_AGENCIES} agencies at once; ${result.count} are selected.`,
            });
            return;
          case 'not-visible':
            // The selection references rows that are not on the current page.
            Notify.error({
              message: 'All selected agencies must be visible on the current page to merge.',
            });
            return;
          case 'husk-block':
            Notify.error({ message: result.message });
            capturePosthogEvent(POSTHOG_EVENTS.agencyMergeBlockedHusk, {
              agency_ids: result.selectedIds,
              husk_ids: result.huskIds,
              target_ids: result.targetIds,
            });
            return;
          case 'inactive-block':
            Notify.error({ message: result.message });
            return;
          case 'ok':
            merge.openWithAgencies(result.selected);
            return;
        }
      }),
    [agencies, merge.openWithAgencies, mergeFlagValue],
  );

  return { merge, bulkActions };
}
